import React from 'react';
import { Radio, DatePicker, Select } from 'antd';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { getSmartLabelClassName } from '../../../../../utils/labelWidthUtils';
import { useFilterContainerClassName, useFilterLabelClassName, useFilterInputContainerClassName } from '../../../../../utils/languageUtils';
import ParcelFilterStyle from '../../../../delivery/components/ParcelFilterStyle';
import { type ArrivalPointType, ARRIVAL_POINT_TYPE_WHS, ARRIVAL_POINT_TYPE_HUB } from '../constants';

export interface HubSlaMonitoringFilters {
    arrivalPointType: ArrivalPointType;
    arrivalDate: dayjs.Dayjs;
    arrivalTimeDateRange: [dayjs.Dayjs, dayjs.Dayjs] | null;
    arrivalTimeDatePreset: 'today' | 'yesterday' | 'last7days' | 'last15days' | 'custom' | null;
    selectedWarehouses: string[];
    selectedDsps: string[];
    viewMode: 'dsp' | 'whs';
}

interface HubSlaMonitoringFilterProps {
    filters: HubSlaMonitoringFilters;
    onFiltersChange: (filters: HubSlaMonitoringFilters) => void;
    dsps: Array<{ id: number; name: string }>;
    warehouses: Array<{ id: number; name: string }>;
}

const HubSlaMonitoringFilter: React.FC<HubSlaMonitoringFilterProps> = ({
    filters,
    onFiltersChange,
    dsps,
    warehouses,
}) => {
    const { t, i18n } = useTranslation();

    const labelArrivalTimeType = String(t('wms.delivery.parcelSlaMonitoring.buttons.arrivalTimeType'));
    const labelArrivalTimeDate = String(t('wms.delivery.parcelSlaMonitoring.buttons.arrivalTimeDate'));
    const labelWhs = String(t('wms.delivery.dailyTaskMonitoring.buttons.whs'));
    const labelDsp = String(t('wms.delivery.parcelSlaMonitoring.buttons.dsp'));

    const col1Labels = [labelArrivalTimeType, labelWhs];
    const col2Labels = [labelArrivalTimeDate, labelDsp];
    const col1ContainerClassName = useFilterContainerClassName();
    const col1LabelClassName = useFilterLabelClassName(getSmartLabelClassName(col1Labels));
    const col2LabelClassName = useFilterLabelClassName(getSmartLabelClassName(col2Labels));
    const col1InputContainerClassName = useFilterInputContainerClassName();
    const col2ContainerClassName = useFilterContainerClassName();
    const col2InputContainerClassName = useFilterInputContainerClassName();
    const dateCol3MarginLeft = i18n.language?.startsWith('zh') ? '-5rem' : '3rem';

    const getDisabledDateRange = (current: dayjs.Dayjs | null) => {
        if (!current) return false;
        const today = dayjs().startOf('day');
        const minDate = today.subtract(30, 'day');
        return current.isAfter(today) || current.isBefore(minDate);
    };

    const handleArrivalTimeDatePreset = (preset: 'today' | 'yesterday' | 'last7days' | 'last15days') => {
        const today = dayjs().startOf('day');
        let range: [dayjs.Dayjs, dayjs.Dayjs];
        switch (preset) {
            case 'today':
                range = [today.clone(), today.clone()];
                break;
            case 'yesterday': {
                const yesterday = today.subtract(1, 'day');
                range = [yesterday.clone(), yesterday.clone()];
                break;
            }
            case 'last7days': {
                const yesterday = today.subtract(1, 'day');
                range = [yesterday.subtract(6, 'day').clone(), yesterday.clone()];
                break;
            }
            case 'last15days': {
                const yesterday = today.subtract(1, 'day');
                range = [yesterday.subtract(14, 'day').clone(), yesterday.clone()];
                break;
            }
            default:
                range = [today.clone(), today.clone()];
        }
        onFiltersChange({
            ...filters,
            arrivalTimeDateRange: range,
            arrivalTimeDatePreset: preset,
            arrivalDate: range[1],
        });
    };

    const isCustom = filters.arrivalTimeDatePreset === 'custom';
    const currentPreset = filters.arrivalTimeDatePreset || 'today';

    return (
        <div className="mb-2 pl-0 py-2">
            <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1">
                    <div className="grid gap-x-[2.5rem] gap-y-4 items-start mb-4" style={{ gridTemplateColumns: 'calc((100% - 5rem) / 3) calc((100% - 5rem) / 3) calc((100% - 5rem) / 3 - 20px)' }}>
                        <div className="min-w-0 w-full">
                            <div className={col1ContainerClassName}>
                                <span className={col1LabelClassName}>{labelArrivalTimeType}</span>
                                <div className={col1InputContainerClassName}>
                                    <Select
                                        value={filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS}
                                        onChange={(value: ArrivalPointType) => onFiltersChange({ ...filters, arrivalPointType: value })}
                                        className="w-full"
                                        size="middle"
                                        placeholder={labelArrivalTimeType || undefined}
                                        allowClear={false}
                                    >
                                        <Select.Option value={ARRIVAL_POINT_TYPE_WHS}>{t('wms.delivery.parcelSlaMonitoring.columns.arrivalTimeTypeWhs')}</Select.Option>
                                        <Select.Option value={ARRIVAL_POINT_TYPE_HUB}>{t('wms.delivery.parcelSlaMonitoring.columns.arrivalTimeTypeHub')}</Select.Option>
                                    </Select>
                                </div>
                            </div>
                        </div>
                        <div className="min-w-0 w-full">
                            <div className={`${col2ContainerClassName} w-full`}>
                                <span className={col2LabelClassName}>{labelArrivalTimeDate}</span>
                                <div className={col2InputContainerClassName}>
                                    <Radio.Group
                                        value={currentPreset}
                                        optionType="button"
                                        buttonStyle="solid"
                                        size="middle"
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === 'custom') {
                                                const today = dayjs().startOf('day');
                                                const currentRange = filters.arrivalTimeDateRange || [today, today];
                                                onFiltersChange({
                                                    ...filters,
                                                    arrivalTimeDateRange: [currentRange[0].clone(), currentRange[1].clone()],
                                                    arrivalTimeDatePreset: 'custom',
                                                });
                                            } else {
                                                handleArrivalTimeDatePreset(value);
                                            }
                                        }}
                                        className="w-full flex flex-nowrap [&_.ant-radio-button-wrapper]:whitespace-nowrap [&_.ant-radio-button-wrapper]:!px-2 [&_.ant-radio-button-wrapper]:!py-1 [&_.ant-radio-button-wrapper]:!flex-1 [&_.ant-radio-button-wrapper]:!flex [&_.ant-radio-button-wrapper]:!items-center [&_.ant-radio-button-wrapper]:!justify-center [&_.ant-radio-button-wrapper-checked]:!bg-white [&_.ant-radio-button-wrapper-checked]:!border [&_.ant-radio-button-wrapper-checked]:!border-[#722ed1] [&_.ant-radio-button-wrapper-checked]:!text-[#722ed1]"
                                    >
                                        <Radio.Button value="today">{t('wms.delivery.parcelSlaMonitoring.buttons.dateRangeToday')}</Radio.Button>
                                        <Radio.Button value="yesterday">{t('wms.delivery.parcelSlaMonitoring.buttons.dateRangeYesterday')}</Radio.Button>
                                        <Radio.Button value="last7days">{t('wms.delivery.parcelSlaMonitoring.buttons.dateRangeLast7Days')}</Radio.Button>
                                        <Radio.Button value="last15days">{t('wms.delivery.parcelSlaMonitoring.buttons.dateRangeLast15Days')}</Radio.Button>
                                        <Radio.Button value="custom">{t('wms.delivery.parcelSlaMonitoring.buttons.dateRangeCustom')}</Radio.Button>
                                    </Radio.Group>
                                </div>
                            </div>
                        </div>
                        <div className="min-w-0 w-full" style={{ marginLeft: dateCol3MarginLeft }}>
                            <div className={`${col2ContainerClassName} w-full`}>
                                <span className={col2LabelClassName} aria-hidden style={{ visibility: 'hidden' }}>{labelArrivalTimeDate}</span>
                                <div className={col2InputContainerClassName}>
                                    <DatePicker.RangePicker
                                        value={filters.arrivalTimeDateRange}
                                        onChange={(dates: [Dayjs | null, Dayjs | null] | null) => {
                                            if (dates && dates[0] && dates[1]) {
                                                onFiltersChange({
                                                    ...filters,
                                                    arrivalTimeDateRange: [dates[0].startOf('day'), dates[1].startOf('day')],
                                                    arrivalTimeDatePreset: 'custom',
                                                    arrivalDate: dates[1].startOf('day'),
                                                });
                                            }
                                        }}
                                        className="w-full"
                                        size="middle"
                                        format="YYYY-MM-DD"
                                        disabledDate={getDisabledDateRange}
                                        placeholder={[labelArrivalTimeDate || '', labelArrivalTimeDate || '']}
                                        allowClear={false}
                                        disabled={!isCustom}
                                    />
                                </div>
                            </div>
                        </div>
                        {/* 第二行：WHS（在 DSP 前）、DSP）；仅未选时展示为空（placeholder），已选全部时仍展示为全部选中；实际请求未选时传全部 warehouseIds */}
                        <div className="min-w-0 w-full">
                            <ParcelFilterStyle
                                label={labelWhs}
                                options={warehouses.map((whs) => ({ value: String(whs.id), label: whs.name }))}
                                value={filters.selectedWarehouses}
                                onChange={(value: string[]) => onFiltersChange({ ...filters, selectedWarehouses: value })}
                                className="w-full"
                                spanClassName={getSmartLabelClassName(col1Labels)}
                                mode="multiple"
                                showSearch
                                placeholder={labelWhs || undefined}
                            />
                        </div>
                        <div className="min-w-0 w-full">
                            <ParcelFilterStyle
                                label={labelDsp}
                                options={dsps.map(dsp => ({ value: dsp.name, label: dsp.name }))}
                                value={filters.selectedDsps}
                                onChange={(value: string[]) => onFiltersChange({ ...filters, selectedDsps: value })}
                                className="w-full"
                                spanClassName={getSmartLabelClassName(col2Labels)}
                                mode="multiple"
                                showSearch
                                placeholder={labelDsp || undefined}
                            />
                        </div>
                        <div className="min-w-0" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HubSlaMonitoringFilter;
